import { ArrowLeft } from 'lucide-react'

import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths } from '@/features/loans/loan-paths'
import { useRunAgeing, useRunClassification } from '@/features/loans/mutations'
import {
  AgeingRunPanel,
  ClassificationRunPanel,
  ClassificationSchemesPanel,
} from '@/features/loans/portfolio-panels'

/**
 * Arrears, provisioning, and the schemes they are measured against.
 *
 * It refuses a narrow display. Classification posts the movement in the provision straight into
 * the ledger, and the mobile policy puts the ledger and manual journals in its refuse table: the
 * whole picture has to be visible at once. Ageing charges penalty against every live round at the
 * branch, which is the same kind of act.
 */
export function LoanPortfolioPage() {
  const ageing = useRunAgeing()
  const classification = useRunClassification()

  return (
    <RequiresWiderScreen
      screen="The loan portfolio"
      reason="These runs charge penalty across a branch and post the provision movement to the ledger. The whole picture has to be visible at once."
    >
      <LoanLink
        to={loanPaths.accounts}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All loans
      </LoanLink>

      <PageHeader
        title="Portfolio"
        description="Ageing, classification and provisioning — the two nightly runs, and the scheme they are measured against."
      />

      <Alert tone="info">
        <AlertTitle>These runs ordinarily happen overnight</AlertTitle>
        <AlertDescription>
          This screen brings one forward: at a month end, after a correction, or when the nightly
          job did not complete. Both are safe to repeat for the same date — running ageing twice
          for one day charges penalty once.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 lg:grid-cols-2">
        <AgeingRunPanel
          run={ageing.data}
          isPending={ageing.isPending}
          error={ageing.isError ? ageing.error : null}
          onRun={(asOf, branchId) => {
            ageing.mutate({ asOf, branchId })
          }}
        />

        <ClassificationRunPanel
          run={classification.data}
          isPending={classification.isPending}
          error={classification.isError ? classification.error : null}
          onRun={(asOf, branchId) => {
            classification.mutate({ asOf, branchId })
          }}
        />
      </div>

      <ClassificationSchemesPanel />
    </RequiresWiderScreen>
  )
}
